// Widget asistencia de empleados (barras de colores) en Dashboard

import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, List, ListItem, LinearProgress, Box, Tooltip } from "@mui/material";
import { useUser } from '../UserContext';
import { getAttendanceStatus } from './AttendanceStatus';
import { usePage } from '../PageContext';
import { apiFetchJson } from '../fetchWrapper';
import { formatDashedDate } from '../util/DateUtil';

export default function EmployeeWidget() {
  const [employees, setEmployees] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [sites, setSites] = useState([]);
  const { setPage } = usePage();

  useEffect(() => {
    const now = new Date();

    // Si la hora es anterior a las 7:00, usar el día de ayer
    if (now.getHours() < 7) {
      now.setDate(now.getDate() - 1);
    }

    const dateStr = formatDashedDate(now);
    apiFetchJson("/api/employees").then(setEmployees);
    apiFetchJson("/api/shift-templates").then(setShiftTemplates);
    apiFetchJson(`/api/attendances?startDate=${dateStr}`).then(setAttendances);
    apiFetchJson(`/api/scheduled-shifts?startDate=${dateStr}`).then(setShifts);
    apiFetchJson(`/api/sites`).then(setSites);
  }, []);

  const getRemainingTime = (shift) => {
    if (!shift || !shift.start || !shift.end) return "";

    const now = new Date();

    // antes del turno
    if (now < shift.start) {
      const diffMs = shift.start - now;

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      return `Empieza en ${hours}h ${minutes}m`;
    }

    // durante turno
    if (now >= shift.start && now <= shift.end) {
      const diffMs = shift.end - now;

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      return `Faltan ${hours}h ${minutes}m para terminar turno`;
    }

    return "Turno finalizado";
  };

  // Calcula el progreso del turno en base al tiempo actual, inicio y fin del turno
  const getShiftTimeInfo = (shift) => {
    if (!shift || !shift.start || !shift.end) {
      return {
        isBeforeStart: false,
        isDuringShift: false,
        isAfterEnd: true,
        progress: 0,
        remainingMs: 0,
        timeToStartMs: 0
      };
    }

    const now = new Date();
    const totalDuration = shift.end - shift.start;
    const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

    // Antes del turno
    if (now < shift.start) {
      const timeToStartMs = shift.start - now;
      const progress = Math.min(Math.round((timeToStartMs / TWELVE_HOURS_MS) * 100), 100);
      return {
        isBeforeStart: true,
        isDuringShift: false,
        isAfterEnd: false,
        progress: (100 - progress),
        remainingMs: timeToStartMs,
        timeToStartMs: timeToStartMs
      };
    }

    // Durante turno
    if (now >= shift.start && now <= shift.end) {
      const elapsedMs = now - shift.start;
      const progress = Math.round((elapsedMs / totalDuration) * 100);
      return {
        isBeforeStart: false,
        isDuringShift: true,
        isAfterEnd: false,
        progress: Math.min(progress, 100),
        remainingMs: shift.end - now,
        timeToStartMs: 0
      };
    }

    // después del turno
    return {
      isBeforeStart: false,
      isDuringShift: false,
      isAfterEnd: true,
      progress: 100,
      remainingMs: 0,
      timeToStartMs: 0
    };
  };

  const handleClick = () => {
    setPage('attendance');
  };

  const getShiftDateTime = (shift) => {
    const template = shiftTemplates.find(t => t.id === shift.shiftTemplateId);
    if (!template) return null;

    const start = new Date(`${shift.shiftDate}T${template.startTime}`);
    let end = new Date(`${shift.shiftDate}T${template.endTime}`);

    // es el siguente día
    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    return { start, end };
  };

  const getActiveShift = (employeeShifts) => {
    const now = new Date();

    const shiftsWithTime = employeeShifts
      .map(s => {
        const times = getShiftDateTime(s, shiftTemplates);
        if (!times) return null;
        return { ...s, ...times };
      })
      .filter(Boolean);

    const past = shiftsWithTime
      .filter(s => s.end < now)
      .sort((a, b) => b.start - a.start);

    const future = shiftsWithTime
      .filter(s => s.start >= now)
      .sort((a, b) => a.start - b.start);

    const current = shiftsWithTime.find(
      s => s.start <= now && s.end >= now
    );

    return current || future[0] || past[0] || null;
  };

  const formatShiftTime = (shift) => {
    if (!shift) return "Sin turno";

    const format = (d) =>
      d.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

    return `${format(shift.start)} - ${format(shift.end)}`;
  };

  const getAttendanceForShift = (shift) => {
    if (!shift) return null;
    const safeAttendances = Array.isArray(attendances) ? attendances : [];
    return safeAttendances.find(a => a.scheduledShiftId === shift.id) || null;
  };

  const populateEmployeeData = () => {
    const safeEmployees = Array.isArray(employees) ? employees : [];
    const safeShifts = Array.isArray(shifts) ? shifts : [];

    return safeEmployees.map(emp => {
      const employeeShifts = safeShifts.filter(
        s => s.employeeId === emp.id
      );

      const activeShift = getActiveShift(employeeShifts);
      const attendance = getAttendanceForShift(activeShift);
      const attendancesStatus = getAttendanceStatus(attendance?.attendanceStatus);

      return {
        ...emp,
        status: attendance ? attendance.attendanceStatus : "UNKNOWN",
        checkIn: attendance ? attendance.checkIn : "-",
        shift: formatShiftTime(activeShift),
        activeShift: activeShift,
        color: attendancesStatus.color,
        label: attendancesStatus.label
      };
    });
  };

  const getEmployeeShiftSiteName = (emp) => {
    const site = sites.find(site => site.id === emp?.activeShift?.siteId);
    return site?.name ?? null;
  };


  const countProgress = (emp) => {
    if (!emp.activeShift) return 0;
    const timeInfo = getShiftTimeInfo(emp.activeShift);
    return timeInfo.progress;
  }

  return (
    <Card elevation={3}
      sx={{
        height: 325,
        borderRadius: 3,
        backgroundColor: '#eef0f2',
        color: 'black',
        overflow: 'auto',

        "&::-webkit-scrollbar": {
          width: "15px"
        },
        "&::-webkit-scrollbar-track": {
          background: "#e0e0e0"
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgb(201, 201, 201)",
          borderRadius: "10px"
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#7d7d7d"
        }
      }} onClick={handleClick}
    >
      <CardContent>
        <Typography variant="h6" fontSize={25} fontWeight={"bold"} gutterBottom>
          Asitencias de Empleados
        </Typography>

        <List>
          {populateEmployeeData().map((emp) => {

            return (
              <ListItem key={emp.id} divider sx={{ display: "block" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>

                  <Box sx={{ minWidth: 0, flex: "0 0 140px" }}>
                    <Typography
                      variant="subtitle1"
                      noWrap
                      sx={{
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        flexShrink: 0
                      }}
                    >
                      {emp.user.person.firstname} {emp.user.person.lastnameFirst}
                    </Typography>
                  </Box>

                  <Tooltip
                    arrow
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                          📍 {getEmployeeShiftSiteName(emp) || "Sin ubicacion"}
                        </Typography>

                        <Typography variant="caption" display="block">
                          {getRemainingTime(emp.activeShift)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ flex: 1, position: "relative", cursor: "pointer" }}>
                      <LinearProgress
                        variant="determinate"
                        value={countProgress(emp)}
                        color={emp.color}
                        sx={{
                          height: 25,
                          backgroundColor: emp.color
                        }}
                      />
                      <Typography
                        variant='caption'
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "5%",
                          transform: "translateY(-50%)",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: 11,
                          pointerEvents: "none",
                          textShadow: "0 0 5px rgba(70, 70, 70, 0.7)",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {emp.label} | {emp.shift}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
}
